<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useSwipe, type UseSwipeDirection } from "@vueuse/core";
import { useAnalyticManager } from "@magnit/analytic-events";
import type {
  IDisplayShop,
  IShopSelectDisplayFilter,
  IShopSelectShopsProps,
} from "../types";
import { ShopModalState, ShopSelectShopType, ShopSelectState } from "../enums";
import {
  LABEL_EMPTY_LIST_DESC_SHOPS,
  LABEL_LIST_SHOPS_SEARCH_TITLE,
  LABEL_LIST_SHOPS_TITLE,
  LABEL_SELECTED_SHOP_TITLE,
  LABEL_SHOP_SEARCH_PLACEHOLDER,
  SHOPS_DETAILED_LIST_ITEMS_OFFSET,
  SHOPS_LIST_ITEM_WITH_TITLE_MAIN_HEIGHT,
  SHOPS_LIST_ITEM_WITH_TITLE_SECONDARY_HEIGHT,
  SHOPS_LIST_SCROLL_DISABLE_TIME,
} from "../constants";
import VSearch from "@magnit/core/src/containers/VSearch/VSearch.vue";
import CloseIcon from "@magnit/icons/src/assets/icons/close-24.svg";
import BackIcon from "@magnit/icons/src/assets/icons/arrow-left-corner-24.svg";
import ShopSelectShopFilters from "./ShopSelectShopFilters.vue";
import ShopSelectListLoading from "./ShopSelectListLoading.vue";
import ShopSelectList from "./ShopSelectList.vue";
import ShopSelectListEmpty from "./ShopSelectListEmpty.vue";

const props = withDefaults(defineProps<IShopSelectShopsProps>(), {
  open: false,
  state: ShopSelectState.READY,
  shops: () => [],
  filters: () => [],
});
const emit = defineEmits<{
  "submit:searchValue": [value: string];
  "click:modal-close": [];
  "click:list-item": [shopCode: string];
  "click:list-item-select": [shopCode: string];
}>();
const refToVSearch = ref<InstanceType<typeof VSearch> | null>(null);

const searchValue = defineModel<string>("searchValue", { default: "" });
const filterValue = defineModel<ShopSelectShopType[]>("filterValue", {
  default: [],
});
const modalState = defineModel<ShopModalState>("modalState", {
  default: ShopModalState.NORMAL,
});

const shopModalOuterRef = ref(null);
useSwipe(shopModalOuterRef, {
  passive: true,
  onSwipeEnd(e: TouchEvent, direction: UseSwipeDirection) {
    // на мобиле, после свайпа вверх и раскрытия БШ, отскролливает вверх, чтобы нивелировать последствия автоскролла (в Сафари при фокусе в инпуте)
    if (direction === "up" && modalState.value !== ShopModalState.MAXIMIZED) {
      setModalExpanded(ShopModalState.MAXIMIZED);
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      }, 50);
    }
  },
});

const { send } = useAnalyticManager();
const listKeyRef = ref(new Date().getTime());
const scrollBlockToken = ref<ReturnType<typeof setTimeout> | null>(null);
const displayShops = computed<IDisplayShop[]>(() => {
  if (!props.shops?.length) return [];
  const list = [...props.shops];
  if (list[0].selected) {
    list[0] = {
      ...list[0],
      title: LABEL_SELECTED_SHOP_TITLE,
      approxHeight: SHOPS_LIST_ITEM_WITH_TITLE_MAIN_HEIGHT,
      className: "shop-select__list-item--title-main",
    };
    if (list.length > 1) {
      list[1] = {
        ...list[1],
        title: searchValue.value
          ? LABEL_LIST_SHOPS_SEARCH_TITLE
          : LABEL_LIST_SHOPS_TITLE,
        approxHeight: SHOPS_LIST_ITEM_WITH_TITLE_SECONDARY_HEIGHT,
        className: "shop-select__list-item--title-secondary",
      };
    }
  } else {
    list[0] = {
      ...list[0],
      title: searchValue.value
        ? LABEL_LIST_SHOPS_SEARCH_TITLE
        : LABEL_LIST_SHOPS_TITLE,
      approxHeight: SHOPS_LIST_ITEM_WITH_TITLE_MAIN_HEIGHT,
      className: "shop-select__list-item--title-main",
    };
  }
  const lastEl = list[list.length - 1];
  lastEl.className = ["shop-select__list-item--last", lastEl.className]
    .filter(Boolean)
    .join(" ");
  lastEl.approxHeight -= SHOPS_DETAILED_LIST_ITEMS_OFFSET;
  return list;
});
const displayListLoading = computed(
  () => props.state === ShopSelectState.LOADING,
);
const displayListEmpty = computed(
  () => !displayListLoading.value && props.total === 0,
);
const displayFilters = computed(() => props.filters.length > 2);
const displayFiltersList = computed<IShopSelectDisplayFilter[]>(() => {
  if (!displayFilters.value) return [];
  return props.filters.map(({ type, label }) => {
    const active =
      !!(type === null && filterValue.value.length === 0) ||
      !!(type && filterValue.value.includes(type));
    return {
      type,
      label,
      active,
      color: active ? "invert-primary" : "primary",
    };
  });
});
const displaySearchFieldCancelButton = computed(
  () => modalState.value === ShopModalState.MAXIMIZED,
);

const disableScrollHandler = (ts = SHOPS_LIST_SCROLL_DISABLE_TIME) => {
  scrollBlockToken.value = setTimeout(() => {
    scrollBlockToken.value = null;
  }, ts);
};
const handleSearchInputSubmit = (val = "") => emit("submit:searchValue", val);
const handleSearchInputClear = () => handleSearchInputSubmit("");
const handleSearchInputCancel = () => {
  handleSearchInputClear();
  disableScrollHandler();
  setModalExpanded(ShopModalState.NORMAL);
};
const handleFilterToggle = (ev: {
  filter: IShopSelectDisplayFilter;
  value: boolean;
}) => {
  const type = ev.filter.type;
  let newVal = [...filterValue.value];
  if (ev.value === true && type === null) newVal.length = 0;
  else if (ev.value === true && type !== null) newVal.push(type);
  else newVal = newVal.filter((s) => s !== type);
  filterValue.value = newVal;
};
const handleItemClick = (shopCode: string) => {
  disableScrollHandler();
  emit("click:list-item", shopCode);
};
const submitSelectedShop = (shopCode: string) => {
  emit("click:list-item-select", shopCode);
};
const setModalExpanded = (val: ShopModalState) => (modalState.value = val);
const handleSearchCancel = () => {
  disableScrollHandler();
  setModalExpanded(ShopModalState.NORMAL);
};
const handleModalClose = () => emit("click:modal-close");
const handleModalHandleClick = () => {
  if (modalState.value !== ShopModalState.MINIMIZED) return;
  modalState.value = ShopModalState.NORMAL;
};

watch(
  () => props.shops,
  () => {
    listKeyRef.value = new Date().getTime();
  },
);

watch(displayListEmpty, (v) => {
  if (!v) return;
  send("ShopSelect:Error:SearchStore:View", { query: searchValue.value });
});
</script>

<template>
  <section
    ref="shopModalOuterRef"
    class="shop-select__shop-modal__outer"
    :class="{ 'shop-select__shop-modal__open': open }"
  >
    <div class="shop-select__shop-modal">
      <div
        class="shop-select__shop-modal--handle"
        @click="handleModalHandleClick"
      >
        <div class="shop-select__shop-modal--handle-element" />
      </div>
      <div class="shop-select__shop-modal--top-nav">
        <div class="shop-select__shop-modal--top-nav--back">
          <BackIcon @click="handleSearchCancel" />
        </div>
        <div class="shop-select__shop-modal--top-nav--close">
          <CloseIcon @click="handleModalClose" />
        </div>
      </div>
      <div class="shop-select__shop-modal--slot">
        <section class="shop-select__shops__outer">
          <div class="shop-select__shops">
            <div
              class="shop-select__shops--search-form"
              data-test-id="shops-search-row"
            >
              <VSearch
                ref="refToVSearch"
                v-model="searchValue"
                class="shop-select__shops--search-form-input"
                :submit-visible="false"
                :placeholder="LABEL_SHOP_SEARCH_PLACEHOLDER"
                :cancel-visible="displaySearchFieldCancelButton"
                @submit="handleSearchInputSubmit"
                @clear="handleSearchInputClear"
                @cancel="handleSearchInputCancel"
              />
            </div>
            <ShopSelectListLoading v-if="displayListLoading" />
            <template v-else>
              <div
                v-if="displayFilters"
                class="shop-select__shops--filters-form"
              >
                <ShopSelectShopFilters
                  :filters="displayFiltersList"
                  @toggle:filter="handleFilterToggle"
                />
              </div>
              <ShopSelectListEmpty
                v-if="displayListEmpty"
                :icon="listEmptyComponent"
                :desc="LABEL_EMPTY_LIST_DESC_SHOPS"
              />
              <ShopSelectList
                v-else
                :key="listKeyRef"
                :list="displayShops"
                :display-fields="displayFields"
                :open="open"
                @select:shop="handleItemClick"
                @submit:shop="submitSelectedShop"
              />
            </template>
          </div>
        </section>
      </div>
    </div>
  </section>
</template>

<style lang="postcss" scoped>
.shop-select__shop-modal {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--pl-surface-primary-default);
  box-shadow: 0 0 10px 0 #00000026;
  border-radius: 24px 24px 0 0;
  padding-top: var(--pl-unit-x5);
  position: relative;

  @media (--pl-viewport-from-ml) {
    border-radius: 24px;
    padding-top: var(--pl-unit-x4);
    overflow: hidden;
  }

  &--handle {
    display: flex;
    position: absolute;
    padding-bottom: var(--pl-unit-x5);
    top: -20px;
    width: 100%;
    height: 43px;
    align-items: center;
    justify-content: center;

    &-element {
      width: 44px;
      height: 4px;
      border-radius: 1000px;
      background: #fff;
    }

    @media (--pl-viewport-from-ml) {
      display: none;
    }
  }

  &__outer {
    position: absolute;
    z-index: 30;
    transition: height 0.35s;
    bottom: 0;
    left: 0;
    right: 0;
    top: unset;
    width: 100%;
    height: 334px;

    @media (--pl-viewport-from-ml) {
      top: 32px;
      bottom: 32px;
      left: 32px;
      width: 360px;
      height: auto;
      display: none;
    }
  }

  &__open {
    display: unset;
  }

  &--top-nav {
    min-height: 44px;
    padding: 0 var(--pl-unit-x4);
    justify-content: space-between;
    align-items: center;
    display: flex;
    display: none;

    @media (--pl-viewport-from-ml) {
      display: flex;
      min-height: 25px;
      padding-bottom: var(--pl-unit-x3);
    }

    &--back > svg {
      display: none;
    }

    &--close {
      display: none;

      @media (--pl-viewport-from-ml) {
        display: unset;
      }
    }

    svg {
      cursor: pointer;
    }
  }

  &--slot {
    position: relative;
    flex-grow: 1;
    overflow: hidden;
  }
}

.shop-select__shops {
  display: flex;
  flex-direction: column;
  align-content: flex-start;
  height: 100%;

  &__outer {
    height: 100%;
    position: absolute;
    inset: 0;
  }

  &--search-form {
    padding: var(--pl-unit-x2) var(--pl-unit-x4);
    display: flex;
    align-items: center;
  }

  &--filters-form {
    padding: 0 0 0 var(--pl-unit-x5);

    @media (--pl-viewport-from-m) {
      padding: 0 var(--pl-unit-x5);
    }
  }
}
</style>
